import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageTransition from "../components/PageTransition";

interface Flower {
  name: string;
  slug: string;
  emoji: string;
  title: string;
  color: string;
}

const FLOWER_GRADIENTS: Record<string, string> = {
  red: "linear-gradient(135deg, #FEE2E2, #FECDD3)",
  rose: "linear-gradient(135deg, #FFE4E6, #FEE2E2)",
  yellow: "linear-gradient(135deg, #FEF9C3, #FDE68A)",
  orange: "linear-gradient(135deg, #FFEDD5, #FEE2E2)",
  purple: "linear-gradient(135deg, #EDE9FE, #E0E7FF)",
  pink: "linear-gradient(135deg, #FCE7F3, #FAE8FF)",
  amber: "linear-gradient(135deg, #FEF3C7, #FEF9C3)",
  lime: "linear-gradient(135deg, #ECFCCB, #D9F99D)",
};

const Blossoms: React.FC = () => {
  const [flowers, setFlowers] = useState<Flower[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFlowers = async () => {
      try {
        const response = await fetch("/api/flowers");
        if (!response.ok) throw new Error("Failed to load flowers");
        const data = await response.json();
        setFlowers(data.flowers);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load flowers");
      } finally {
        setLoading(false);
      }
    };

    fetchFlowers();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-brand-50 to-yellow-25 dark:from-gray-900 dark:to-gray-800">
        <p className="text-gray-600 dark:text-gray-400">Loading flowers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-brand-50 to-yellow-25 dark:from-gray-900 dark:to-gray-800">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-gradient-to-b from-brand-50 to-yellow-25 dark:from-gray-900 dark:to-gray-800 py-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-slide-up">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              The Blossoms
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Explore all eight flower personalities and discover what makes each one unique.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {flowers.map((flower, index) => (
              <Link
                key={flower.slug}
                to={`/results?slug=${flower.slug}`}
                className={`group rounded-2xl shadow-lg p-6 text-center transition-all duration-300 hover:shadow-xl hover:scale-105 animate-slide-up animation-fill-both dark:bg-gray-800`}
                style={{
                  background: FLOWER_GRADIENTS[flower.color] || FLOWER_GRADIENTS.purple,
                  animationDelay: `${100 + index * 50}ms`,
                }}
              >
                <div className="text-6xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {flower.emoji}
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {flower.name}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 italic">
                  {flower.title}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default Blossoms;
